// Telefone é a chave de deduplicação do funil (PLANO 4.2), então precisa de
// uma forma canônica: E.164 brasileiro.

/** Converte qualquer entrada digitada em E.164. Devolve '' se não houver dígitos. */
export function normalizarTelefone(entrada) {
  const bruto = String(entrada ?? '').trim();
  if (!bruto) return '';
  if (bruto.startsWith('+')) return '+' + bruto.slice(1).replace(/\D/g, '');

  const digitos = bruto.replace(/\D/g, '');
  if (!digitos) return '';
  // 10 dígitos (fixo com DDD) ou 11 (celular com DDD) → falta só o DDI.
  if (digitos.length === 10 || digitos.length === 11) return `+55${digitos}`;
  return `+${digitos}`;
}

/**
 * Exibe +5551991230001 como (51) 99123-0001.
 *
 * A máscara com DDD só vale para o Brasil: aplicá-la a qualquer número de 11
 * dígitos transformaria o americano +1 415 555 2671 em "(14) 15555-2671".
 * Números de outros países saem como estão.
 */
export function formatarTelefone(e164) {
  const texto = String(e164 ?? '');
  const digitos = texto.replace(/\D/g, '');
  if (!digitos.startsWith('55')) return texto;

  const nacional = digitos.slice(2);
  if (nacional.length === 11) {
    return `(${nacional.slice(0, 2)}) ${nacional.slice(2, 7)}-${nacional.slice(7)}`;
  }
  if (nacional.length === 10) {
    return `(${nacional.slice(0, 2)}) ${nacional.slice(2, 6)}-${nacional.slice(6)}`;
  }
  return texto;
}

/**
 * Celular brasileiro decomposto, ou null se não for um.
 *
 * `+55` + DDD (dois dígitos, nenhum deles 0) + a parte local. Celular tem a
 * parte local começando em 6–9: 8 dígitos no formato antigo, 9 no atual (que
 * sempre começa com 9). Fixo começa em 2–5.
 */
function celularBr(e164) {
  const m = /^\+55([1-9][1-9])([0-9]{8,9})$/.exec(String(e164 ?? ''));
  if (!m) return null;

  const [, ddd, parte] = m;
  if (parte.length === 8) {
    // 8 dígitos começando em 6–9 é o celular ANTIGO, sem o nono dígito.
    return /^[6-9]/.test(parte) ? { ddd, sem9: parte, com9: `9${parte}` } : null;
  }
  // 9 dígitos: só é celular se começar com 9. Qualquer outra coisa com esse
  // tamanho não é número brasileiro conhecido — melhor não adivinhar.
  if (!parte.startsWith('9')) return null;
  return { ddd, sem9: parte.slice(1), com9: parte };
}

/**
 * Forma canônica de um telefone: para celular brasileiro, SEMPRE com o nono
 * dígito. Fixo e número de outro país voltam intactos.
 *
 * Gêmea de `canonicalTelefone` em `functions/messaging/telefone.js` — a mesma
 * duplicação deliberada do resto deste arquivo, porque o deploy das Functions
 * empacota só a pasta `functions/`. **As duas precisam concordar**: é ela que
 * faz o telefone da agenda em papel e o do WhatsApp caírem na mesma conversa.
 * A agenda do Careflow escreve muito celular sem o nono dígito.
 */
export function canonicalTelefone(e164) {
  const cel = celularBr(e164);
  return cel ? `+55${cel.ddd}${cel.com9}` : String(e164 ?? '');
}

/**
 * Todas as grafias equivalentes de um telefone, para procurar no banco.
 *
 * Gêmea de `variantesTelefone` em `functions/messaging/telefone.js`, pela mesma
 * duplicação deliberada do resto deste arquivo. **As duas precisam concordar** —
 * há teste comparando o comportamento.
 *
 * Para celular brasileiro devolve as DUAS grafias (com e sem o nono dígito),
 * porque o `contatoTelefone` guarda o número como o provedor o entregou, e o
 * WhatsApp às vezes manda sem o 9. Buscar só a forma digitada deixaria metade
 * das conversas antigas inalcançável.
 *
 * O teto de 10 é o limite do operador `in` do Firestore; estourá-lo faria a
 * query lançar.
 */
export function variantesTelefone(e164) {
  const numero = String(e164 ?? '');
  const cel = celularBr(numero);
  if (!cel) return numero ? [numero] : [];
  return [...new Set([`+55${cel.ddd}${cel.com9}`, `+55${cel.ddd}${cel.sem9}`])].slice(0, 10);
}

/**
 * As variantes a procurar a partir do que a pessoa DIGITOU na busca.
 *
 * Diferente do `variantesTelefone`: ali entra um E.164 já formado; aqui entra
 * texto solto — "62 99664-0861", "6299664 0861", "+5562996640861". Monta o
 * E.164 quando dá para reconhecer o formato brasileiro e devolve as duas
 * grafias; devolve lista vazia quando o termo não parece telefone, e é isso que
 * evita disparar uma query por cada palavra que o atendente digita.
 */
export function variantesDaBusca(termo) {
  const digitos = String(termo ?? '').replace(/\D/g, '');
  // Menos de 10 dígitos não identifica ninguém: DDD (2) + número (8 ou 9).
  if (digitos.length < 10) return [];

  // Já veio com o 55 na frente (colado de outro lugar, ou com +55).
  const semPais = digitos.startsWith('55') && digitos.length >= 12 ? digitos.slice(2) : digitos;
  if (semPais.length < 10 || semPais.length > 11) return [];

  return variantesTelefone(`+55${semPais}`);
}

/**
 * ★★★ WT lote 1b — CELULAR BRASILEIRO PLAUSÍVEL: `+55` + DDD + o 9 + 8 dígitos.
 *
 * ⚠⚠ **Cópia deliberada de `functions/messaging/telefone.js`** — o deploy das Functions
 * empacota só a pasta `functions/`, então um lado não importa o outro. ★ Há **teste de
 * espelho** que importa os DOIS arquivos e passa as mesmas entradas pelos dois
 * (`web/src/lib/telefoneEspelho.test.js`): se a regra mudar de um lado só, ele cai.
 *
 * ★★ POR QUE O WEB PRECISA DISTO AGORA: o `DialogoIniciarConversa` chamava o servidor com o
 * que a pessoa digitasse, e **só lá** o número era recusado. ⚠ O primeiro erro registrado em
 * produção (06/out 07:32, a Jéssica) foi exatamente isso: uma viagem até a Cloud Function
 * para devolver *"Informe um celular com DDD"*. ★ Cada viagem dessas custa um registro e uma
 * espera — e a pessoa descobre o erro longe do campo onde o cometeu.
 *
 * ⚠ **O servidor continua validando.** A autoridade é dele; isto é conveniência e economia.
 *
 * ★ Recusa FIXO de propósito: WhatsApp de linha fixa existe, mas é raro e não é o caso de
 * uso — e aceitar abriria a porta para o erro de digitação comum (esquecer o 9). O DDD não
 * pode ter 0 em nenhum dos dois dígitos.
 */
export const PADRAO_CELULAR_BR = /^\+55[1-9][1-9]9[0-9]{8}$/;

export const ehCelularBrasileiro = (e164) => PADRAO_CELULAR_BR.test(String(e164 ?? ''));
